/**
 * SmartEWM Operational Assistant — workspace layout.
 *
 * Refreshed Feb 2026 toward an operational assistant workspace (per the
 * uploaded design brief) while preserving the existing PRIVA backend
 * contract. The page still posts to `/api/priva/smartewm/ask` for every
 * question — the redesign only re-arranges the surface around that core
 * experience.
 *
 * Layout (mirrors the reference image):
 *   1. Page header                        — title + subtitle + alerts pill
 *   2. Operational summary strip          — Warehouse / Shift / Next Wave / Open Risks
 *   3. PRIVA intro card                   — welcome message + active alert count
 *   4. Suggested question cards           — five operational shortcuts
 *   5. Conversation thread                — appears once the supervisor asks
 *   6. Recent PRIVA insights              — static placeholder cards (frontend only)
 *   7. Critical Alerts side panel         — top 4 active alerts (toggleable)
 *   8. Composer pinned at the bottom      — Ask PRIVA input
 *
 * Data sources:
 *   - GET /api/priva/smartewm/summary   → warehouse / wave / health / alert count
 *   - GET /api/priva/smartewm/alerts    → active alerts
 *   - POST /api/priva/smartewm/ask      → conversational answer
 *
 * Data rules (per product brief):
 *   - We never claim live status for replenishment / labor / wave timing
 *     unless the backend has surfaced it. Source-awareness metadata stays
 *     in governance audit, not in the UI.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { getMySmartewmContext } from "../lib/adminApi";
import EnvBadge, { ClientLogo } from "../components/EnvBadge";
import { systemLabel } from "../lib/smartewmLabels";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Send,
  Sparkles,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Bot,
  User,
  ChevronDown,
  Activity,
  Users,
  Truck,
  HelpCircle,
  Lightbulb,
  Package,
  Paperclip,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEMO_TENANT_DEFAULTS = {
  clientId: "demo-client",
  userEmail: "supervisor@demo-ewm.com",
  applicationAccess: "SmartEWM",
  applicationRole: "Supervisor",
};

function buildTenantPayload(user, ctx) {
  return {
    clientId: ctx?.clientId || user?.client_id || DEMO_TENANT_DEFAULTS.clientId,
    sapSystem: ctx?.clientSapId || null,
    userEmail: user?.email || DEMO_TENANT_DEFAULTS.userEmail,
    applicationAccess:
      user?.application_access || DEMO_TENANT_DEFAULTS.applicationAccess,
    applicationRole:
      user?.application_role || DEMO_TENANT_DEFAULTS.applicationRole,
  };
}

// Chat starter prompts surfaced as compact chips (Copilot / ChatGPT style).
// Phrased in supervisor / operational-domain language (Tier-1 domains).
const SUGGESTED_QUESTIONS = [
  { label: "What needs attention now?", icon: AlertTriangle },
  { label: "How is outbound flowing?", icon: Truck },
  { label: "Any inventory shortages?", icon: Package },
  { label: "Is execution workload under control?", icon: Activity },
  { label: "What should I prioritize first?", icon: Users },
];

const RISK_STYLES = {
  Critical: "bg-rose-50 text-rose-700 border-rose-200",
  High: "bg-rose-50 text-rose-700 border-rose-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-sky-50 text-sky-700 border-sky-200",
  default: "bg-slate-50 text-slate-700 border-slate-200",
};
function riskClass(level) {
  return RISK_STYLES[level] || RISK_STYLES.default;
}

// Tier-1 domain → supervisor-facing risk title + the natural question the
// "Review Risk" shortcut injects into the conversation (CHANGE 1, Phase 5).
const RISK_DOMAIN_META = {
  outbound: { title: "Outbound Execution", question: "Explain the outbound execution risk." },
  inventory: { title: "Inventory Availability", question: "Explain the inventory availability risk." },
  inbound: { title: "Inbound Processing", question: "Explain the inbound processing risk." },
  execution: { title: "Execution Workload", question: "Explain the execution workload risk." },
  exceptions: { title: "Active Exceptions", question: "Explain the active exceptions risk." },
};

/**
 * Derive a coarse "shift" label from the current local time.
 * Backend doesn't surface shift today, so this is a UI-only placeholder.
 */
function deriveShift(now = new Date()) {
  const h = now.getHours();
  if (h >= 6 && h < 14) return "Morning";
  if (h >= 14 && h < 22) return "Afternoon";
  return "Night";
}

export default function SmartEwmBotPage() {
  const outletCtx = useOutletContext() || {};
  const user = outletCtx.user || null;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  // Operational Domain Briefing — supervisor-language Tier-1 domain health +
  // business agent status (GET /api/priva/smartewm/briefing).
  const [briefing, setBriefing] = useState(null);
  // SmartEWM runtime context — the Client + SAP System this session targets.
  // Drives every PRIVA call (Signal Resolver / SAP Connection Resolver / PRIVA).
  const [ewmContext, setEwmContext] = useState(null);
  const [selected, setSelected] = useState({ clientId: "", clientSapId: "" });
  const [switching, setSwitching] = useState(false);
  // Streaming-feel UX: a live phase-status line during the (slow) live-SAP +
  // LLM wait, then a token-by-token reveal of the answer.
  const [thinkingStatus, setThinkingStatus] = useState("");
  const [typing, setTyping] = useState(false);
  const [alerts, setAlerts] = useState([]);
  // Last suggested question the supervisor tapped — used purely for a
  // subtle visual highlight so the cards persist (per Feb 2026 brief)
  // but the supervisor can still tell which one was just clicked.
  const [activeSuggestion, setActiveSuggestion] = useState(null);

  const scrollRef = useRef(null);
  // Stable per-conversation session id — enables backend Conversation Memory
  // (multi-turn follow-ups) and the streaming endpoint.
  const sessionIdRef = useRef(
    `sb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  async function fetchSummary() {
    try {
      const tenant = buildTenantPayload(user, selected);
      const res = await axios.get(`${API}/priva/smartewm/summary`, {
        params: { clientId: tenant.clientId },
      });
      setSummary(res.data || null);
    } catch (e) {
      setSummary(null);
    }
  }

  async function fetchAlerts() {
    try {
      const tenant = buildTenantPayload(user, selected);
      const res = await axios.get(`${API}/priva/smartewm/alerts`, {
        params: { clientId: tenant.clientId },
      });
      setAlerts(Array.isArray(res.data?.alerts) ? res.data.alerts : []);
    } catch (e) {
      setAlerts([]);
    }
  }

  // Operational Domain Briefing — Tier-1 domain health + business agent status.
  // The Agent refreshes operational data automatically (on load + after every
  // question) — there is no manual refresh.
  async function fetchBriefing() {
    try {
      const tenant = buildTenantPayload(user, selected);
      const res = await axios.get(`${API}/priva/smartewm/briefing`, {
        params: {
          clientId: tenant.clientId,
          sapSystem: tenant.sapSystem || undefined,
        },
      });
      setBriefing(res.data || null);
    } catch (e) {
      setBriefing(null);
    }
  }

  // Load the SmartEWM runtime context (allowed Clients + SAP Systems) and
  // pin the default selection. Business-friendly names only.
  useEffect(() => {
    getMySmartewmContext()
      .then((ctx) => {
        setEwmContext(ctx);
        const defClient = ctx.defaultClientId
          || (ctx.clients && ctx.clients[0] && ctx.clients[0].clientId)
          || "";
        const match = (ctx.clients || []).find((c) => c.clientId === defClient);
        const defSys = ctx.defaultSapSystem
          || (match && match.systems && match.systems[0] && match.systems[0].clientSapId)
          || "";
        setSelected({ clientId: defClient, clientSapId: defSys });
      })
      .catch(() => setEwmContext({ clients: [], isAdmin: false }));
  }, []);

  // Load summary + alerts + proactive feeds whenever the selected context
  // changes. NOTE: live-SAP feeds serialize on the backend (one SAP session),
  // so we fire `summary` FIRST — it feeds the always-visible health pill +
  // operational strip and should resolve before the heavier feeds. The
  // "switching" indicator only shows for a USER-initiated swap (guarded by a
  // ref) so it never sticks on the initial page load.
  const didInitContext = useRef(false);
  useEffect(() => {
    if (!selected.clientId) return;
    const userSwitch = didInitContext.current;
    didInitContext.current = true;
    let cancelled = false;
    if (userSwitch) setSwitching(true);
    setError("");
    // Prioritise the visible feeds (summary then alerts) ahead of the
    // proactive rails so the strip/pill paint as early as the SAP queue allows.
    fetchSummary().finally(() => {
      if (!cancelled) setSwitching(false);
      fetchBriefing();
      fetchAlerts();
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.clientId, selected.clientSapId]);

  // Phase-status messages shown while the agent waits on live SAP + the LLM.
  // They cycle on a timer so the (~20-30s) wait reads as active work, not a
  // dead spinner. Tied to the real pipeline phases.
  const AGENT_PHASES = [
    "Checking SAP EWM live signals…",
    "Reading wave data…",
    "Reviewing stock availability…",
    "Reviewing queue and outbound status…",
    "Preparing recommendation…",
  ];

  // Reveal the answer token-by-token (word chunks) so it types out like a
  // live agent rather than appearing all at once.
  async function typeOutAnswer(fullAnswer, meta) {
    const text = fullAnswer || "PRIVA returned an empty response.";
    const tokens = text.split(/(\s+)/); // keep whitespace tokens
    setTyping(true);
    setMessages((prev) => [
      ...prev,
      { role: "assistant", answer: "", meta, streaming: true },
    ]);
    let acc = "";
    for (let i = 0; i < tokens.length; i += 1) {
      acc += tokens[i];
      const snapshot = acc;
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") {
          copy[copy.length - 1] = { ...last, answer: snapshot };
        }
        return copy;
      });
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 28));
    }
    setMessages((prev) => {
      const copy = [...prev];
      const last = copy[copy.length - 1];
      if (last && last.role === "assistant") {
        copy[copy.length - 1] = { ...last, streaming: false };
      }
      return copy;
    });
    setTyping(false);
  }

  async function askPriva(question) {
    const q = (question || "").trim();
    if (!q || loading || typing) return;
    setError("");
    setActiveSuggestion(q);
    setMessages((prev) => [...prev, { role: "user", answer: q, meta: null }]);
    setInput("");
    setLoading(true);
    setThinkingStatus(AGENT_PHASES[0]);

    const payload = {
      question: q,
      sessionId: sessionIdRef.current,
      ...buildTenantPayload(user, selected),
    };

    try {
      await streamAsk(payload);
    } catch (e) {
      // Streaming unavailable / interrupted before any answer surfaced →
      // fall back to the non-streaming endpoint so the supervisor still gets
      // a reply (no regression vs. the previous behaviour).
      if (e?.message === "stream_fallback") {
        await fallbackAsk(payload);
      } else {
        const detail =
          e?.message && e.message !== "stream_error"
            ? e.message
            : "The agent is unreachable right now. Please try again.";
        setError(typeof detail === "string" ? detail : "Unexpected error.");
      }
      setLoading(false);
      setThinkingStatus("");
    } finally {
      // Refresh operational context + signals so any state changes surface.
      fetchSummary();
      fetchBriefing();
      fetchAlerts();
    }
  }

  // Real backend streaming over SSE: live pipeline status, then progressive
  // answer tokens straight from the server (no frontend-simulated typing).
  async function streamAsk(payload) {
    // Watchdog: if the stream goes idle (no status/token/done) for too long,
    // abort so the supervisor is never locked out of the composer.
    const controller = new AbortController();
    const IDLE_MS = 60000;
    let idleTimer = null;
    const resetIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => controller.abort(), IDLE_MS);
    };
    resetIdle();

    let resp;
    try {
      resp = await fetch(`${API}/priva/smartewm/ask/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
    } catch (err) {
      if (idleTimer) clearTimeout(idleTimer);
      throw new Error("stream_fallback");
    }
    if (resp.status === 403) {
      if (idleTimer) clearTimeout(idleTimer);
      setLoading(false);
      setThinkingStatus("");
      setError("You do not have access to this SAP system.");
      return;
    }
    if (!resp.ok || !resp.body) {
      if (idleTimer) clearTimeout(idleTimer);
      throw new Error("stream_fallback");
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let placeholderAdded = false;
    let doneReceived = false;

    const ensurePlaceholder = () => {
      if (placeholderAdded) return;
      placeholderAdded = true;
      setLoading(false);
      setThinkingStatus("");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", answer: "", meta: {}, streaming: true },
      ]);
    };
    const updateLastAssistant = (mutate) => {
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") {
          copy[copy.length - 1] = mutate(last);
        }
        return copy;
      });
    };

    const handleEvent = (ev) => {
      if (ev.type === "status") {
        setThinkingStatus(ev.message || "");
      } else if (ev.type === "token") {
        ensurePlaceholder();
        updateLastAssistant((last) => ({
          ...last,
          answer: (last.answer || "") + (ev.text || ""),
          streaming: true,
        }));
      } else if (ev.type === "done") {
        ensurePlaceholder();
        doneReceived = true;
        updateLastAssistant((last) => ({
          ...last,
          answer: ev.answer ?? last.answer,
          meta: {
            riskLevel: ev.riskLevel || null,
            recommendedAction: ev.recommendedAction || null,
            actionPlan: ev.actionPlan || null,
          },
          streaming: false,
        }));
      } else if (ev.type === "error") {
        if (!placeholderAdded) throw new Error("stream_fallback");
        throw new Error(ev.message || "stream_error");
      }
    };

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        resetIdle();
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (raw === "[DONE]") continue;
          let ev;
          try {
            ev = JSON.parse(raw);
          } catch {
            continue;
          }
          handleEvent(ev);
        }
      }
    } catch (err) {
      // Aborted/interrupted mid-stream.
      if (!placeholderAdded && !doneReceived) {
        if (idleTimer) clearTimeout(idleTimer);
        throw new Error("stream_fallback");
      }
      // Partial answer already shown — finalize it rather than locking the UI.
      updateLastAssistant((last) => ({ ...last, streaming: false }));
    } finally {
      if (idleTimer) clearTimeout(idleTimer);
    }

    // Stream ended without ever producing an answer → fall back.
    if (!placeholderAdded && !doneReceived) {
      throw new Error("stream_fallback");
    }
    // Stream closed without an explicit 'done' — finalize the bubble.
    if (placeholderAdded && !doneReceived) {
      updateLastAssistant((last) => ({ ...last, streaming: false }));
    }
    setLoading(false);
    setThinkingStatus("");
  }

  // Non-streaming fallback (kept for resilience if SSE is unavailable).
  async function fallbackAsk(payload) {
    const res = await axios.post(`${API}/priva/smartewm/ask`, payload);
    const data = res.data || {};
    setLoading(false);
    setThinkingStatus("");
    await typeOutAnswer(data.answer, {
      riskLevel: data.riskLevel || null,
      recommendedAction: data.recommendedAction || null,
      actionPlan: data.actionPlan || null,
    });
  }

  function onSubmit(e) {
    e.preventDefault();
    askPriva(input);
  }

  // ---------------------------------------------------------------------
  // Critical-alerts dataset (single source of truth for top pill + rail).
  //
  // For the Feb 2026 MVP the brief asks both surfaces to show the SAME
  // "Critical" count. The backend currently stores severity as
  // Critical / High / Medium / Low — and most live signals today land at
  // "High". We treat {Critical, High} as the supervisor-visible
  // "Critical" bucket so the count between the header pill and the
  // right-rail panel always matches the rows the supervisor can see.
  //
  // TODO: Once the backend ships per-severity rollups (Critical / High /
  // Medium / Low), switch this to a strict severity === "Critical"
  // filter and surface the other tiers as their own grouped rails.
  // ---------------------------------------------------------------------
  const criticalAlerts = useMemo(() => {
    const list = Array.isArray(alerts) ? alerts : [];
    return list.filter((a) =>
      ["Critical", "High"].includes(a?.severity),
    );
  }, [alerts]);
  const criticalCount = criticalAlerts.length;

  // PRIVA Recommendations are now sourced from the backend
  // `/api/priva/smartewm/recommendations` endpoint (Feb 2026 phase 1
  // proactive intelligence layer). The previous client-side
  // `deriveRecommendations` helper has been removed — the rail reads
  // straight from state.

  // Derive the top strip metrics. We always show the four tiles; missing
  // backend fields fall back to safe placeholders rather than 'N/A'.
  //
  // `alertCount` here is intentionally the SAME critical-bucket count
  // used by the header pill and right rail — never the raw rollup —
  // so "Open Risks" / pill / rail never disagree.
  //
  // ``wave`` only carries a concrete value when the backend marks it as
  // live-sourced (`outboundWaveLive: true`). Until SAP wave OData is
  // wired up the strip shows "Awaiting SAP wave data" rather than the
  // placeholder "Wave-42" string the simulation produces.
  const stripMetrics = useMemo(() => {
    const liveAvailable = summary?.liveSignalAvailable === true;
    const wh = liveAvailable ? summary?.warehouse || "EWM-100" : "No live signal";
    const waveLive = summary?.outboundWaveLive === true;
    const wave =
      waveLive && summary?.outboundWave
        ? summary.outboundWave
        : "Awaiting SAP wave data";
    const health = summary?.overallHealth || "Stable";
    const shift = deriveShift();
    return {
      wh,
      wave,
      waveLive,
      alertCount: criticalCount,
      health,
      shift,
      liveAvailable,
    };
  }, [summary, criticalCount]);

  // Business-friendly name of the SAP system currently in context (for the
  // agent briefing message). Falls back gracefully before context resolves.
  const selectedSystemName = useMemo(() => {
    const clients = ewmContext?.clients || [];
    const c = clients.find((x) => x.clientId === selected.clientId) || clients[0];
    const sys = (c?.systems || []).find((s) => s.clientSapId === selected.clientSapId)
      || (c?.systems || [])[0];
    return sys ? systemLabel(sys) : "SAP EWM";
  }, [ewmContext, selected]);

  const conversationStarted = messages.length > 0;

  // CHANGE 1 (Phase 5): the live critical risks behind the "Open Risks" header
  // chip — drives the clickable flyout. Sourced from the Tier-1 briefing.
  const criticalRisks = useMemo(() => {
    const domains = Array.isArray(briefing?.domains) ? briefing.domains : [];
    return domains.filter((d) => d.status === "critical");
  }, [briefing]);

  return (
    <div
      className="h-full flex flex-col min-h-0"
      data-testid="smartewmbot-page"
    >
      {/* Header */}
      <header
        className="shrink-0 flex items-start justify-between gap-4 mb-4"
        data-testid="smartewmbot-header"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg bg-[#025979] flex items-center justify-center shrink-0">
            <Bot className="w-5 h-5 text-white" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <h1
              className="text-2xl sm:text-3xl font-bold text-[#0F172A] leading-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="smartewmbot-title"
            >
              SmartEWM Agent
            </h1>
            <p
              className="text-sm text-[#475569] mt-1 max-w-2xl"
              data-testid="smartewmbot-subtitle"
            >
              AI-powered warehouse execution agent for SAP EWM operations.
            </p>
          </div>
        </div>

        <AgentStatusPopover
          briefing={briefing}
          summary={summary}
        />
      </header>

      {/* Compact context bar — Client / System + live operational context */}
      <SmartEwmContextBar
        context={ewmContext}
        selected={selected}
        onSelect={setSelected}
        switching={switching}
        metrics={stripMetrics}
        criticalRisks={criticalRisks}
        onReviewRisk={askPriva}
      />

      {/* Body — chat-first, full width. Granular agent signals live in the
          header status popover, so the conversation owns the whole canvas. */}
      <div className="flex-1 min-h-0 flex">
        {/* Agent conversation column */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto pr-1"
            data-testid="smartewmbot-workspace-scroll"
          >
            {/* 3. Agent briefing message (first message in the stream) */}
            <AgentBriefingMessage
              briefing={briefing}
              summary={summary}
              systemName={selectedSystemName}
            />

            {/* 4. Main chat conversation */}
            {conversationStarted && (
              <ConversationThread
                messages={messages}
                loading={loading}
                thinkingStatus={thinkingStatus}
                error={error}
                onAsk={askPriva}
              />
            )}
          </div>

          {/* 5. Suggested prompt chips — pinned just above the input,
              ChatGPT/Copilot starter-prompt style (they never scroll away). */}
          <SuggestedQuestionsBlock
            onAsk={askPriva}
            disabled={loading || typing}
            active={activeSuggestion}
            collapsible={conversationStarted}
          />

          {/* 6. Agent input pinned at the bottom */}
          <Composer
            value={input}
            onChange={setInput}
            onSubmit={onSubmit}
            disabled={loading || typing}
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Runtime context bar — Client + SAP System selector                 */
/* ------------------------------------------------------------------ */

function SmartEwmContextBar({ context, selected, onSelect, switching, metrics, criticalRisks, onReviewRisk }) {
  if (!context || !Array.isArray(context.clients) || context.clients.length === 0) {
    return null;
  }
  const clients = context.clients;
  const activeClient = clients.find((c) => c.clientId === selected.clientId) || clients[0];
  const systems = activeClient?.systems || [];
  const activeSystem = systems.find((s) => s.clientSapId === selected.clientSapId) || systems[0];

  const multiClient = clients.length > 1;
  const multiSystem = systems.length > 1;

  const onClientChange = (cid) => {
    const c = clients.find((x) => x.clientId === cid);
    const sys = c?.systems || [];
    const def = sys.find((s) => s.isDefault) || sys[0];
    onSelect({ clientId: cid, clientSapId: def ? def.clientSapId : "" });
  };
  const onSystemChange = (sid) => onSelect({ clientId: activeClient.clientId, clientSapId: sid });

  return (
    <section
      className="shrink-0 mb-4 flex flex-wrap items-center gap-x-8 gap-y-3 bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 shadow-sm"
      data-testid="smartewmbot-context-bar"
    >
      {/* Client logo */}
      <ClientLogo logo={activeClient.logo} name={activeClient.clientName} size={38} />

      {/* Client */}
      <div className="flex flex-col gap-0.5">
        <span className="text-[9px] uppercase tracking-widest text-[#94A3B8] font-bold">Client</span>
        {multiClient ? (
          <Select value={activeClient.clientId} onValueChange={onClientChange}>
            <SelectTrigger className="h-7 text-xs w-44 border-[#E2E8F0]" data-testid="smartewmbot-context-client">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.clientId} value={c.clientId}>
                  <span className="inline-flex items-center gap-1.5">
                    <ClientLogo logo={c.logo} name={c.clientName} size={16} />{c.clientName}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-sm font-bold text-[#025979]" data-testid="smartewmbot-context-client-static">
            {activeClient.clientName}
          </span>
        )}
      </div>

      <span className="text-[#CBD5E1] text-lg">·</span>

      {/* SAP System */}
      <div className="flex flex-col gap-0.5">
        <span className="text-[9px] uppercase tracking-widest text-[#94A3B8] font-bold">System</span>
        {systems.length === 0 ? (
          <span className="text-xs italic text-[#94A3B8]" data-testid="smartewmbot-context-system-none">
            No SAP system assigned
          </span>
        ) : multiSystem ? (
          <Select value={activeSystem?.clientSapId || ""} onValueChange={onSystemChange}>
            <SelectTrigger className="h-7 text-xs w-56 border-[#E2E8F0]" data-testid="smartewmbot-context-system">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {systems.map((s) => (
                <SelectItem key={s.clientSapId} value={s.clientSapId}>
                  <span className="inline-flex items-center gap-1.5">{systemLabel(s)}<EnvBadge env={s.environmentLabel || s.clientSapId.split("-").pop()} /></span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#025979]" data-testid="smartewmbot-context-system-static">
            {systemLabel(activeSystem)}
            <EnvBadge env={activeSystem?.environmentLabel || activeSystem?.clientSapId?.split("-").pop()} />
          </span>
        )}
      </div>

      {/* Switching indicator */}
      {switching && (
        <span className="inline-flex items-center gap-2 text-xs text-[#025979] font-medium" data-testid="smartewmbot-context-switching">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Switching to {activeSystem ? systemLabel(activeSystem) : activeClient.clientName}…
        </span>
      )}

      {/* Compact live operational context — NOT a KPI dashboard. */}
      {metrics && (
        <div className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-1.5" data-testid="smartewmbot-context-metrics">
          <ContextChip label="Warehouse" value={metrics.wh} muted={!metrics.liveAvailable} testId="ctx-warehouse" />
          <ContextChip label="Shift" value={metrics.shift} testId="ctx-shift" />
          <ContextChip label="Next Wave" value={metrics.wave} muted={!metrics.waveLive} testId="ctx-wave" />
          <CriticalRisksChip
            metrics={metrics}
            criticalRisks={criticalRisks}
            onReviewRisk={onReviewRisk}
          />
        </div>
      )}
    </section>
  );
}

/* Clickable "Open Risks" header chip → critical-risk flyout (CHANGE 1).
 * Clicking a risk's "Review Risk" injects a natural question into the chat —
 * it NEVER navigates to a dashboard or opens a modal. */
function CriticalRisksChip({ metrics, criticalRisks, onReviewRisk }) {
  const [open, setOpen] = useState(false);
  const risks = Array.isArray(criticalRisks) ? criticalRisks : [];
  const liveAvailable = metrics?.liveAvailable === true;

  // No live signal or all-clear → plain, non-clickable chip.
  if (!liveAvailable) {
    return <ContextChip label="Open Risks" value="No live signal" tone="muted" testId="ctx-risks" />;
  }
  if (risks.length === 0) {
    return <ContextChip label="Open Risks" value="All clear" tone="ok" testId="ctx-risks" />;
  }

  const review = (q) => {
    setOpen(false);
    if (onReviewRisk && q) onReviewRisk(q);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex flex-col gap-0.5 text-left rounded-md hover:bg-rose-50/60 px-1 -mx-1 transition-colors"
          data-testid="smartewmbot-ctx-risks"
          aria-label={`${risks.length} critical risks. Open list.`}
        >
          <span className="text-[9px] uppercase tracking-widest text-[#94A3B8] font-bold">Open Risks</span>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600">
            {risks.length} Critical
            <ChevronDown className="w-3 h-3" aria-hidden="true" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 overflow-hidden" data-testid="smartewmbot-critical-risks-flyout">
        <div className="px-4 py-3 border-b border-[#E2E8F0]">
          <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> Critical Risks ({risks.length})
          </p>
        </div>
        <ul className="divide-y divide-[#F1F5F9] max-h-80 overflow-y-auto">
          {risks.map((d, i) => {
            const meta = RISK_DOMAIN_META[d.domain] || {};
            const title = meta.title || d.label || "Operational Risk";
            const finding = Array.isArray(d.findings) && d.findings.length > 0 ? d.findings[0] : null;
            const question = meta.question || `Explain the ${(d.label || "operational").toLowerCase()} risk.`;
            return (
              <li key={d.domain || i} className="px-4 py-3" data-testid={`smartewmbot-critical-risk-${d.domain}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{i + 1}. {title}</p>
                {finding && <p className="text-xs text-[#475569] mt-0.5 leading-snug">{finding}</p>}
                <p className="text-[11px] font-medium text-rose-600 mt-1">Risk: High</p>
                <button
                  type="button"
                  onClick={() => review(question)}
                  className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#025979]/30 bg-white text-[11px] font-semibold text-[#025979] hover:bg-[#025979] hover:text-white transition-colors"
                  data-testid={`smartewmbot-review-risk-${d.domain}`}
                >
                  Review Risk
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function ContextChip({ label, value, muted, tone, testId }) {
  const valueClass = tone === "risk"
    ? "text-rose-600"
    : tone === "ok"
      ? "text-emerald-600"
      : muted || tone === "muted"
        ? "text-[#94A3B8] italic"
        : "text-[#0F172A]";
  return (
    <div className="flex flex-col gap-0.5" data-testid={`smartewmbot-${testId}`}>
      <span className="text-[9px] uppercase tracking-widest text-[#94A3B8] font-bold">{label}</span>
      <span className={`text-xs font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Agent briefing message (first agent turn)                          */
/* ------------------------------------------------------------------ */

function AgentBriefingMessage({ briefing, summary, systemName }) {
  const loading = !briefing;
  const greeting = briefing?.greeting || "Hello.";
  const status = briefing?.agentStatus || {};
  const signalsAvailable = Number(status.signalsAvailable || 0);
  const live = signalsAvailable > 0;
  const warehouse = briefing?.warehouse;
  const domains = Array.isArray(briefing?.domains) ? briefing.domains : [];
  // Surface only domains carrying an operational concern (supervisor-language).
  const riskDomains = domains.filter(
    (d) => d.status === "critical" || d.status === "warning",
  );
  const priority = Array.isArray(briefing?.recommendedPriority)
    ? briefing.recommendedPriority.filter((p) => p !== "Continue normal execution")
    : [];
  const topPriority = priority[0] || null;
  const confidence = briefing?.confidence || null;

  return (
    <section
      className="flex items-start gap-3"
      data-testid="smartewmbot-agent-briefing"
    >
      <div className="w-8 h-8 rounded-full bg-[#025979] flex items-center justify-center shrink-0 mt-0.5">
        <Bot className="w-4 h-4 text-white" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1 bg-white border border-[#E2E8F0] rounded-2xl rounded-tl-sm shadow-sm px-5 py-4">
        <p className="text-sm text-[#0F172A] leading-relaxed">
          {greeting}{" "}
          {loading ? (
            <>I&apos;m reviewing live SAP signals for <span className="font-semibold">{systemName}</span>…</>
          ) : live && warehouse ? (
            <>
              I reviewed <span className="font-semibold">Warehouse {warehouse}</span>{" "}
              in <span className="font-semibold">{systemName}</span>.
            </>
          ) : (
            <>I&apos;m monitoring <span className="font-semibold">{systemName}</span>.</>
          )}
        </p>

        {loading ? (
          <p className="text-sm text-[#64748B] mt-3 inline-flex items-center gap-2" data-testid="smartewmbot-briefing-loading">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" strokeWidth={1.75} />
            Building your operational briefing…
          </p>
        ) : live ? (
          <>
            {riskDomains.length > 0 ? (
              <>
                <p className="text-xs font-semibold text-[#475569] mt-3 mb-1.5">
                  What I found
                </p>
                <ul className="space-y-2" data-testid="smartewmbot-briefing-domains">
                  {riskDomains.map((d) => (
                    <li key={d.domain} data-testid={`smartewmbot-briefing-domain-${d.domain}`}>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${d.status === "critical" ? "bg-rose-500" : "bg-amber-500"}`} />
                        {d.label}
                      </span>
                      <ul className="mt-1 ml-3.5 space-y-0.5">
                        {(d.findings || []).slice(0, 2).map((f, i) => (
                          <li key={i} className="text-sm text-[#334155]">{f}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm text-[#334155] mt-3 inline-flex items-center gap-2" data-testid="smartewmbot-briefing-allclear">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={1.75} />
                All operational domains are stable — no active risks detected.
              </p>
            )}

            {topPriority && (
              <div className="mt-3 flex items-start gap-2 bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg px-3 py-2" data-testid="smartewmbot-briefing-priority">
                <CheckCircle2 className="w-4 h-4 text-[#025979] shrink-0 mt-0.5" strokeWidth={1.75} />
                <p className="text-sm text-[#0F172A]">
                  <span className="font-semibold">Recommended priority: </span>{topPriority}
                </p>
              </div>
            )}

            {confidence?.level && (
              <div className="mt-3 flex items-center gap-2" data-testid="smartewmbot-briefing-confidence">
                <ConfidenceBadge level={confidence.level} />
                {confidence.basis && (
                  <span className="text-[11px] text-[#94A3B8]">{confidence.basis}</span>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-[#64748B] italic mt-2">
            {briefing?.intro
              || "No live SAP signal is available for this system yet."}{" "}
            Ask me a question and I&apos;ll analyze whatever execution data SAP returns.
          </p>
        )}

        {!loading && (
          <p className="text-sm text-[#0F172A] mt-3 font-medium">
            {briefing?.closing || "What would you like me to analyze?"}
          </p>
        )}
        <LiveSyncIndicator liveSync={summary?.liveSync} />
      </div>
    </section>
  );
}

const CONFIDENCE_STYLES = {
  High: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};

function ConfidenceBadge({ level }) {
  const cls = CONFIDENCE_STYLES[level] || CONFIDENCE_STYLES.Low;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
      data-testid="smartewmbot-confidence-badge"
      data-confidence={level}
    >
      <Activity className="w-3 h-3" strokeWidth={2} />
      {level} confidence
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Header "Agent Signals" popover (status pill + granular signals)    */
/* ------------------------------------------------------------------ */

function AgentStatusPopover({ briefing, summary }) {
  if (!briefing) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-[#64748B]"
        data-testid="smartewmbot-health-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading agent status…
      </span>
    );
  }

  const status = briefing.agentStatus || {};
  const signalsAvailable = Number(status.signalsAvailable || 0);
  const activeRisks = Number(status.activeRisks || 0);
  const live = signalsAvailable > 0;
  const confidence = status.confidence || "Low";
  const currentFocus = status.currentFocus || "Stable";
  const lastSyncAt = status.lastSyncAt;
  // A stale cache served because a live refresh failed (backend flags this).
  const refreshIssue = briefing.refreshStatus === "error";
  const syncLabel = live
    ? (lastSyncAt ? formatSyncAge(lastSyncAt) : "live")
    : "no live signal";

  const dot = live ? "bg-emerald-500" : "bg-amber-500";
  const border = live ? "border-emerald-200" : "border-amber-200";
  const text = live ? "text-emerald-700" : "text-amber-700";

  const rows = [
    { label: "Last SAP Sync", value: lastSyncAt ? formatSyncAge(lastSyncAt).replace("synced ", "") : "—", testId: "last-sap-sync" },
    { label: "Signals Available", value: String(signalsAvailable), testId: "signals-available" },
    { label: "Active Risks", value: String(activeRisks), testId: "active-risks", danger: activeRisks > 0 },
    { label: "Current Focus", value: currentFocus, testId: "current-focus" },
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Agent status. ${signalsAvailable} signals available, ${activeRisks} active risks, ${confidence} confidence.`}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border ${border} text-xs font-medium ${text} hover:shadow-sm transition-all`}
          data-testid="smartewmbot-health-pill"
          data-health={summary?.overallHealth || (live ? "Live" : "Offline")}
        >
          <span className={`w-2 h-2 rounded-full ${dot}`} aria-hidden="true" />
          <span data-testid="smartewmbot-health-label">
            {live ? "SAP Live" : "SAP Offline"}
          </span>
          <span className="text-[#CBD5E1]">·</span>
          <span className="text-[#64748B] font-normal" data-testid="smartewmbot-pill-summary">
            {signalsAvailable} signals · {syncLabel}
          </span>
          <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-0 overflow-hidden"
        data-testid="smartewmbot-signals-panel"
      >
        <div className="px-4 py-3 border-b border-[#E2E8F0]">
          <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
            <Activity className="w-3.5 h-3.5 text-[#025979]" /> Agent Status
          </p>
          <p className="text-[11px] text-[#94A3B8] mt-0.5">
            Live operational readiness
          </p>
        </div>
        <ul className="divide-y divide-[#F1F5F9]">
          {rows.map((r) => (
            <li
              key={r.label}
              className="px-4 py-2.5"
              data-testid={`smartewmbot-status-${r.testId}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#334155]">{r.label}</span>
                <span className={`text-[11px] font-semibold ${r.danger ? "text-rose-600" : "text-[#475569]"}`}>
                  {r.value}
                </span>
              </div>
              {r.testId === "last-sap-sync" && refreshIssue && (
                <p
                  className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-600"
                  data-testid="smartewmbot-status-refresh-issue"
                >
                  <AlertTriangle className="w-3 h-3 shrink-0" strokeWidth={2} />
                  Refresh issue detected
                </p>
              )}
            </li>
          ))}
          <li className="px-4 py-2.5 flex items-center justify-between" data-testid="smartewmbot-status-confidence">
            <span className="text-xs text-[#334155]">Confidence</span>
            <ConfidenceBadge level={confidence} />
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Subtle "SAP LIVE • synced 14s ago" indicator pinned to the bottom
 * of the intro card. Reads from `summary.liveSync` populated by the
 * backend `_build_live_sync_meta` helper. We never expose hostnames /
 * credentials — the indicator is intentionally cosmetic and
 * source-aware:
 *   * `liveSync.live === true`     → "SAP LIVE · synced 14s ago"
 *   * `dataSource === "sap-fallback"` → "SAP fallback · simulation"
 *   * everything else              → no indicator (avoids
 *     misrepresenting placeholder data as live).
 */
function LiveSyncIndicator({ liveSync }) {
  if (!liveSync) return null;
  const ds = liveSync.dataSource;
  if (!liveSync.live && ds !== "sap-fallback") {
    return null;
  }
  const label =
    liveSync.live ? "SAP LIVE" : "SAP fallback";
  const dot =
    liveSync.live ? "bg-emerald-500" : "bg-amber-500";
  const text =
    liveSync.live ? "text-emerald-700" : "text-amber-700";
  return (
    <p
      className={`mt-3 inline-flex items-center gap-1.5 text-[10px] font-medium ${text}`}
      data-testid="smartewmbot-livesync"
      data-live={liveSync.live ? "true" : "false"}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden="true" />
      <span className="uppercase tracking-wider">{label}</span>
      <span className="text-[#94A3B8]">·</span>
      <span className="text-[#475569] normal-case tracking-normal">
        {formatSyncAge(liveSync.syncedAt)}
      </span>
    </p>
  );
}

function formatSyncAge(syncedAt) {
  if (!syncedAt) return "awaiting first sync";
  const ts = new Date(syncedAt).getTime();
  if (Number.isNaN(ts)) return "awaiting first sync";
  const ageMs = Date.now() - ts;
  if (ageMs < 1500) return "synced just now";
  const sec = Math.floor(ageMs / 1000);
  if (sec < 60) return `synced ${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `synced ${min}m ago`;
  const hr = Math.floor(min / 60);
  return `synced ${hr}h ago`;
}

/* ------------------------------------------------------------------ */
/* Suggested questions                                                */
/* ------------------------------------------------------------------ */

function SuggestedQuestionsBlock({ onAsk, disabled, active, collapsible }) {
  // Starter prompts live just above the input (ChatGPT/Copilot style). Once a
  // conversation is underway they auto-collapse behind a "Suggested prompts"
  // toggle so they stay handy without crowding the chat.
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (collapsible) setOpen(false);
  }, [collapsible]);

  return (
    <section className="shrink-0 mt-3" data-testid="smartewmbot-suggestions">
      {collapsible && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-[#64748B] hover:text-[#025979] mb-2 transition-colors"
          data-testid="smartewmbot-suggestions-toggle"
        >
          <Lightbulb className="w-3 h-3" />
          Suggested prompts
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {open && (
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_QUESTIONS.map((q, i) => {
            const Icon = q.icon;
            const isActive = active === q.label;
            return (
              <button
                key={q.label}
                type="button"
                onClick={() => onAsk(q.label)}
                disabled={disabled}
                aria-pressed={isActive}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isActive
                    ? "border-[#025979] bg-[#025979] text-white"
                    : "border-[#E2E8F0] bg-white text-[#334155] hover:border-[#025979]/50 hover:text-[#025979]"
                }`}
                data-testid={`smartewmbot-suggestion-${i}`}
                data-active={isActive ? "true" : "false"}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                {q.label}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Conversation thread                                                */
/* ------------------------------------------------------------------ */

function ConversationThread({ messages, loading, thinkingStatus, error, onAsk }) {
  return (
    <section
      className="mt-5 space-y-5"
      data-testid="smartewmbot-messages"
    >
      {messages.map((m, idx) => (
        <ChatBubble key={idx} message={m} index={idx} onAsk={onAsk} />
      ))}
      {loading && (
        <div
          className="flex items-center gap-2.5 text-xs text-[#475569] pl-11"
          data-testid="smartewmbot-loading"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#025979] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#025979]" />
          </span>
          <span data-testid="smartewmbot-thinking-status">
            {thinkingStatus || "Agent is analyzing live SAP signals…"}
          </span>
        </div>
      )}
      {error && (
        <div
          className="flex items-start gap-2 text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#991B1B]/20 rounded-md px-3 py-2"
          data-testid="smartewmbot-error"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </section>
  );
}

function ChatBubble({ message, index, onAsk }) {
  const isUser = message.role === "user";
  const [copied, setCopied] = useState(false);
  const meta = message.meta || {};
  const plan = meta.actionPlan || null;
  const isAgentAnswer = !isUser && (meta.riskLevel || meta.recommendedAction || plan);
  const isStreaming = !!message.streaming;

  const copyRecommendation = () => {
    const text = meta.recommendedAction || message.answer || "";
    if (navigator.clipboard && text) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  return (
    <div
      className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}
      data-testid={`smartewmbot-msg-${index}`}
      data-role={message.role}
    >
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
          isUser ? "bg-[#334155] text-white" : "bg-[#025979] text-white"
        }`}
      >
        {isUser ? (
          <User className="w-4 h-4" strokeWidth={1.75} />
        ) : (
          <Bot className="w-4 h-4" strokeWidth={1.75} />
        )}
      </div>
      <div className={`max-w-[88%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
            isUser
              ? "bg-[#025979] text-white rounded-2xl rounded-tr-sm"
              : "bg-white text-[#0F172A] rounded-2xl rounded-tl-sm border border-[#E2E8F0] shadow-sm"
          }`}
          data-testid={`smartewmbot-msg-${index}-bubble`}
        >
          {message.answer}
          {isStreaming && (
            <span
              className="inline-block w-1.5 h-4 ml-0.5 -mb-0.5 bg-[#025979] animate-pulse align-middle"
              aria-hidden="true"
              data-testid={`smartewmbot-msg-${index}-cursor`}
            />
          )}
        </div>

        {isAgentAnswer && !isStreaming && (
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`smartewmbot-msg-${index}-meta`}>
            {meta.riskLevel && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${riskClass(meta.riskLevel)}`}
                data-testid={`smartewmbot-msg-${index}-risk`}
              >
                Risk · {meta.riskLevel}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-[11px] text-[#64748B]" data-testid={`smartewmbot-msg-${index}-source`}>
              <Activity className="w-3 h-3" /> SAP EWM live signal
            </span>
          </div>
        )}

        {isAgentAnswer && !isStreaming && (
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`smartewmbot-msg-${index}-actions`}>
            <AgentActionButton onClick={() => onAsk && onAsk("Why is this a risk?")} testId={`smartewmbot-msg-${index}-ask-why`}>
              <HelpCircle className="w-3 h-3" /> Ask Why
            </AgentActionButton>
            <AgentActionButton onClick={copyRecommendation} testId={`smartewmbot-msg-${index}-copy`}>
              <CheckCircle2 className="w-3 h-3" /> {copied ? "Copied" : "Copy Recommendation"}
            </AgentActionButton>
          </div>
        )}
      </div>
    </div>
  );
}

function AgentActionButton({ children, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E2E8F0] bg-white text-[11px] font-medium text-[#475569] hover:border-[#025979]/50 hover:text-[#025979] transition-colors"
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Composer (pinned ask input)                                        */
/* ------------------------------------------------------------------ */

function Composer({ value, onChange, onSubmit, disabled }) {
  const onKeyDown = (e) => {
    // Enter to send, Shift+Enter for a new line.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSubmit(e);
    }
  };
  return (
    <form
      onSubmit={onSubmit}
      className="mt-3 shrink-0 bg-white border border-[#E2E8F0] rounded-2xl shadow-sm focus-within:border-[#025979]/60 focus-within:ring-2 focus-within:ring-[#025979]/10 transition-all"
      data-testid="smartewmbot-composer"
    >
      <div className="flex items-end gap-2 px-4 py-2.5">
        <button
          type="button"
          aria-label="Attach file (coming soon)"
          disabled
          className="p-2 rounded-lg text-[#CBD5E1] hover:bg-[#F1F5F9] disabled:cursor-not-allowed shrink-0"
          data-testid="smartewmbot-attach-btn"
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <textarea
          rows={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask the Agent what needs attention, what to prioritize, or why a risk exists…"
          disabled={disabled}
          className="flex-1 resize-none max-h-32 py-1.5 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-[#94A3B8] disabled:opacity-60 leading-relaxed"
          data-testid="smartewmbot-input"
        />
        <button
          type="submit"
          disabled={disabled || !value.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-[#025979] text-white hover:bg-[#024661] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
          data-testid="smartewmbot-send-btn"
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline">Ask Agent</span>
        </button>
      </div>
      <p
        className="px-4 pb-2.5 text-[11px] text-[#94A3B8]"
        data-testid="smartewmbot-disclaimer"
      >
        The Agent references live SAP EWM data. Please review before taking action. · Enter to send, Shift+Enter for a new line.
      </p>
    </form>
  );
}

